import { useListItem, useMergeRefs } from '@floating-ui/react'
import cn from 'classnames'
import React, { useContext } from 'react'

import { MenuContext } from '../lib/utils'
import styles from './MenuItem.module.scss'

interface MenuItemProps extends React.HTMLAttributes<HTMLButtonElement> {
  variant?: 'default' | 'primary'
  before?: React.ReactNode
  after?: React.ReactNode
  isActive?: boolean
  disabled?: boolean
}

function MenuItem(
  { variant = 'default', before, after, children, className, disabled, isActive, ...props }: MenuItemProps,
  forwardRef: React.ForwardedRef<HTMLButtonElement>
) {
  const menu = useContext(MenuContext)
  const item = useListItem()
  const activated = item.index === menu.activeIndex || isActive

  const ref = useMergeRefs([item.ref, forwardRef])

  const handleOnClick = (ev: React.MouseEvent<HTMLButtonElement>) => {
    props.onClick?.(ev)
    if (!menu.preventCloseOnItemClick) {
      menu.setOpened(false)
    }
  }

  return (
    <button
      type="button"
      role="menuitem"
      ref={ref}
      disabled={disabled}
      className={cn(
        styles.root,
        activated && styles.hovered,
        disabled && styles.disabled,
        variant && styles[variant],
        className
      )}
      {...props}
      {...menu.getItemProps({
        onClick: handleOnClick,
      })}
    >
      {before && <span className={styles.before}>{before}</span>}
      <span className={styles.content}>{children}</span>
      {after && <span className={styles.icon}>{after}</span>}
    </button>
  )
}

export default React.forwardRef<HTMLButtonElement, MenuItemProps>(MenuItem)
